import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { defaultGridOptions, GridDimensions } from '../helpers';
import { useGridDimensions } from '../hooks';
import {
  GamesFilterSortDirection,
  GamesFilterSortType,
  GameV1,
} from '@/api/games';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { RestrictedProviders } from '@/helpers/conf';
import { createGamesListName } from '@/helpers/game';
import { useMedia } from '@/hooks';
import { getGridPlugs } from '@/modules/common';
import { GameCardFactory } from '@/modules/game/components/GameCardFactory';
import { ShowAllGameCard } from '@/modules/game/components/ShowAllGameCard';
import { EmptyGameList } from '@/modules/games/components/EmptyGameList';
import { useCMSGameCategories } from '@/modules/lobby/hooks';
import { useCommonStore } from '@/store';

import { ScGamesGrid, ScGamesGridRow } from '../styled';

export interface GamesGridProps extends GridDimensions {
  category?: string;
  provider?: string;
  plugGray?: boolean;
  isSearchModal?: boolean;
  isSmallCard?: boolean;
  onGamesLoad?: (games: GameV1[]) => void;
  onPlayClick?: (game: GameV1) => void;
  onDemoClick?: (game: GameV1) => void;
}

const SUB_NAME = 'list';
const defaultGameInfo = {
  games: [],
  pagination: {
    totalCount: 0,
    currentPage: 0,
    nextPage: 0,
    prevPage: 0,
    totalPages: 0,
  },
};

export const GamesGrid: FC<GamesGridProps> = ({
  category,
  provider,
  perChunkObj,
  gridOptions = defaultGridOptions,
  plugGray = true,
  isSearchModal,
  isSmallCard,
  onGamesLoad,
  onPlayClick,
  onDemoClick,
}) => {
  const [currentChunk, setCurrentChunk] = useState(1);
  const [gamePlugs, setGamePlugs] = useState<null[]>([]);

  const [isLoadingGames, setIsLoadingGames] = useState(true);

  const { categories: gameCategoriesList } = useCMSGameCategories();

  const { isMobile: isMobileDevice } = useMedia();

  const { perChunk, gridColumns } = useGridDimensions({
    perChunkObj,
    gridOptions,
  });

  const {
    gamesByCategories,
    gamesByProviders,
    pendingGamesByProviders,
    pendingGamesByCategories,
    getGamesByCategory,
    getGamesByProvider,
  } = useCommonStore(
    useShallow((state) => ({
      gamesByCategories: state.gamesByCategories,
      gamesByProviders: state.gamesByProviders,
      pendingGamesByProviders: state.pendingGamesByProviders,
      pendingGamesByCategories: state.pendingGamesByCategories,
      getGamesByCategory: state.getGamesByCategory,
      getGamesByProvider: state.getGamesByProvider,
    }))
  );

  const fetchGames = useCallback(
    (page = 1) => {
      const requestParams = {
        page,
        pageSize: perChunk,
        subName: SUB_NAME,
      };

      if (category) {
        getGamesByCategory(category, requestParams).finally(() => {
          setIsLoadingGames(false);
        });
      }

      if (provider) {
        getGamesByProvider(provider, {
          ...requestParams,
          sort: {
            type: GamesFilterSortType.POPULARITY,
            direction: GamesFilterSortDirection.ASC,
          },
        }).finally(() => {
          setIsLoadingGames(false);
        });
      }
    },
    [perChunk, category, provider, getGamesByCategory, getGamesByProvider]
  );

  const gamesListName = useMemo(() => {
    const activeParam = category || provider || '';
    return createGamesListName(activeParam, SUB_NAME);
  }, [category, provider]);

  const { games: gamesChunk, pagination } = useMemo(() => {
    if (category) {
      if (gamesByCategories[gamesListName]) {
        setIsLoadingGames(false);
      }
      return gamesByCategories[gamesListName] || defaultGameInfo;
    }
    if (provider) {
      if (gamesByProviders[gamesListName]) {
        setIsLoadingGames(false);
      }
      return gamesByProviders[gamesListName] || defaultGameInfo;
    }
    return defaultGameInfo;
  }, [gamesListName, category, provider, gamesByCategories, gamesByProviders]);

  useEffect(() => {
    if (!gameCategoriesList.length || pagination.currentPage > 1) return;
    if (
      category &&
      (gamesByCategories[`${category}-list`] ||
        pendingGamesByCategories[`${category}-list`])
    )
      return;
    if (
      provider &&
      (gamesByProviders[`${provider}-list`] ||
        pendingGamesByProviders[`${provider}-list`])
    )
      return;

    setCurrentChunk(1);
    fetchGames();
  }, [
    gamesByCategories,
    pendingGamesByCategories,
    gamesByProviders,
    pendingGamesByProviders,
    category,
    provider,
    gameCategoriesList,
    pagination.currentPage,
    fetchGames,
  ]);

  useEffect(() => {
    if (!onGamesLoad) return;

    const categoryGames = gamesByCategories[gamesListName]?.games;
    const providerGames = gamesByProviders[gamesListName]?.games;

    if (categoryGames?.length) {
      return onGamesLoad(categoryGames);
    }

    if (providerGames?.length) {
      return onGamesLoad(providerGames);
    }
  }, [
    category,
    gamesByCategories,
    gamesByProviders,
    gamesListName,
    onGamesLoad,
    provider,
  ]);

  const gamesToShow = useMemo(() => {
    return gamesChunk.slice(0, currentChunk * perChunk);
  }, [gamesChunk, currentChunk, perChunk]);

  const nextChunk = useCallback(() => {
    setCurrentChunk((s) => s + 1);
    pagination.nextPage !== null && fetchGames(currentChunk + 1);
  }, [pagination.nextPage, fetchGames, currentChunk]);

  const isShowMoreMode = useMemo(() => {
    return pagination.totalCount - gamesToShow.length !== 0;
  }, [pagination.totalCount, gamesToShow.length]);

  useEffect(() => {
    setGamePlugs(getGridPlugs(gamesToShow.length, perChunk, gridColumns));
  }, [gamesToShow.length, perChunk, gridColumns]);

  const isShowAllGameCardVisible = useMemo(() => {
    return pagination.totalCount > perChunk && isShowMoreMode;
  }, [pagination.totalCount, perChunk, isShowMoreMode]);

  const gamesList = useMemo(() => {
    if (isShowAllGameCardVisible) {
      if (gamePlugs.length === 0) {
        return gamesToShow.slice(0, -1);
      }
      return [...gamesToShow, ...gamePlugs.slice(0, -1)];
    }
    return [...gamesToShow, ...gamePlugs];
  }, [isShowAllGameCardVisible, gamesToShow, gamePlugs]);

  const mode = plugGray ? 'dark' : 'light';

  const handleShowAllGameCardClick = useCallback(() => {
    setGamePlugs([]);
    nextChunk();
  }, [nextChunk]);

  const cols = useMemo(() => {
    if (isMobileDevice) {
      return gridOptions.columnsMobile;
    }
    return gridOptions.columns;
  }, [isMobileDevice]);

  return (
    <ScGamesGrid>
      {isLoadingGames ? (
        <GamesSkeleton isSearchModal={isSearchModal} cols={cols} />
      ) : provider === RestrictedProviders.AUTOPAY || !gamesList.length ? (
        <EmptyGameList categoryId={category} />
      ) : (
        <ScGamesGridRow {...gridOptions}>
          {gamesList.map((game) => (
            <Fragment key={`${category || provider}-${game?.id}`}>
              {game?.id ? (
                <GameCardFactory
                  game={game}
                  mode={mode}
                  isSmall={isSmallCard}
                  onPlayClick={() => onPlayClick && onPlayClick(game)}
                  onDemoClick={() => onDemoClick && onDemoClick(game)}
                />
              ) : (
                <GameCardFactory isSmall={isSmallCard} isEmpty />
              )}
            </Fragment>
          ))}
          {isShowAllGameCardVisible && (
            <ShowAllGameCard
              mode={mode}
              isShowMoreMode
              isSmall={isSmallCard}
              onClick={handleShowAllGameCardClick}
            />
          )}
        </ScGamesGridRow>
      )}
    </ScGamesGrid>
  );
};
